/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */

import type {Locale} from 'date-fns';
import moment from 'moment-timezone';
import {formatDate} from '@/core/util/helper/datefns';

export type FormatEmployeePunchTimeOptions = {
  utcTime?: string | null;
  timezoneName?: string | null;
  fallbackTimezoneName?: string | null;
  userTime?: string | null;
  jsDateFormat: string;
  jsTimeFormat: string;
  locale?: Locale;
};

function formatWallClock(
  year: number,
  month: number,
  date: number,
  hour: number,
  minute: number,
  second: number,
  jsDateFormat: string,
  jsTimeFormat: string,
  locale?: Locale,
): string {
  const wallClock = new Date(year, month, date, hour, minute, second);
  const datePart = formatDate(wallClock, jsDateFormat, {locale});
  const timePart = formatDate(wallClock, jsTimeFormat, {locale});

  if (!datePart || !timePart) {
    return '';
  }

  return `${datePart} ${timePart}`;
}

function formatFromUserTimeIso(
  userTime: string,
  jsDateFormat: string,
  jsTimeFormat: string,
  locale?: Locale,
): string {
  const parsed = moment.parseZone(userTime);
  if (!parsed.isValid()) {
    return '';
  }

  return formatWallClock(
    parsed.year(),
    parsed.month(),
    parsed.date(),
    parsed.hour(),
    parsed.minute(),
    parsed.second(),
    jsDateFormat,
    jsTimeFormat,
    locale,
  );
}

export function formatEmployeePunchTime(
  options: FormatEmployeePunchTimeOptions,
): string {
  const {
    utcTime,
    timezoneName,
    fallbackTimezoneName,
    userTime,
    jsDateFormat,
    jsTimeFormat,
    locale,
  } = options;

  const effectiveTimezone = timezoneName || fallbackTimezoneName;

  if (utcTime && effectiveTimezone) {
    const local = moment(utcTime).tz(effectiveTimezone);
    if (local.isValid()) {
      return formatWallClock(
        local.year(),
        local.month(),
        local.date(),
        local.hour(),
        local.minute(),
        local.second(),
        jsDateFormat,
        jsTimeFormat,
        locale,
      );
    }
  }

  if (userTime) {
    return formatFromUserTimeIso(userTime, jsDateFormat, jsTimeFormat, locale);
  }

  if (utcTime) {
    const utc = moment.utc(utcTime);
    if (utc.isValid()) {
      return formatWallClock(
        utc.year(),
        utc.month(),
        utc.date(),
        utc.hour(),
        utc.minute(),
        utc.second(),
        jsDateFormat,
        jsTimeFormat,
        locale,
      );
    }
  }

  return '';
}
